<template>
    <Head :title="`Schedule: ${schedule.title}`" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Header -->
        <div class="flex justify-between items-start mb-6">
            <div>
                <div class="flex items-center space-x-3">
                    <Link
                        :href="route('checklists.schedules.index')"
                        class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    >
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </Link>
                    <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">{{ schedule.title }}</h1>
                </div>
                <p v-if="schedule.description" class="mt-1 text-sm text-gray-600 dark:text-gray-400">{{ schedule.description }}</p>
            </div>
            <div class="flex items-center space-x-3">
                <!-- Pause Button -->
                <button
                    v-if="schedule.status === 'active'"
                    @click="pauseSchedule"
                    class="inline-flex items-center px-4 py-2 bg-orange-600 dark:bg-orange-500 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-orange-700 dark:hover:bg-orange-600 transition"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Pause Schedule
                </button>

                <!-- Resume Button -->
                <button
                    v-if="schedule.status === 'paused'"
                    @click="showResumeModal = true"
                    class="inline-flex items-center px-4 py-2 bg-green-600 dark:bg-green-500 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-green-700 dark:hover:bg-green-600 transition"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Resume Schedule
                </button>

                <Link
                    :href="route('checklists.schedules.edit', schedule.id)"
                    class="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md font-semibold text-xs text-gray-700 dark:text-gray-300 uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-gray-600 transition"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit Schedule
                </Link>
            </div>
        </div>

        <!-- Schedule Details Grid -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <!-- Main Info Card -->
            <div class="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Schedule Information</h2>
                <dl class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Schedule Code</dt>
                        <dd class="mt-1 text-sm text-gray-900 dark:text-white font-mono">{{ schedule.schedule_code }}</dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
                        <dd class="mt-1">
                            <span :class="getStatusClass(schedule.status)">
                                {{ getStatusLabel(schedule.status) }}
                            </span>
                        </dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Template</dt>
                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                            {{ schedule.template?.title }}
                            <span class="text-gray-500 dark:text-gray-400">({{ schedule.template?.template_code }})</span>
                        </dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Recurrence Pattern</dt>
                        <dd class="mt-1 text-sm text-gray-900 dark:text-white capitalize">
                            {{ schedule.recurrence_pattern?.replace('_', ' ') }}
                            <span v-if="schedule.recurrence_config?.interval" class="text-gray-500 dark:text-gray-400">
                                (Every {{ schedule.recurrence_config.interval }} {{ getIntervalLabel(schedule.recurrence_pattern, schedule.recurrence_config.interval) }})
                            </span>
                        </dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Start Date</dt>
                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                            {{ formatDateOnly(schedule.start_date) }}
                        </dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">End Date</dt>
                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                            {{ schedule.end_date ? formatDateOnly(schedule.end_date) : 'No end date' }}
                        </dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Schedule Trigger Time</dt>
                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                            {{ schedule.scheduled_time ? formatTimeOnly(schedule.scheduled_time) : 'N/A' }}
                        </dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Next Execution</dt>
                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                            {{ schedule.next_execution_at ? formatDateAndTime(schedule.next_execution_at) : 'N/A' }}
                        </dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Last Executed</dt>
                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                            {{ schedule.last_executed_at ? formatDateAndTime(schedule.last_executed_at) : 'Never' }}
                        </dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">SLA (Completion Time)</dt>
                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                            {{ formatSLA(schedule.completion_sla_hours) }}
                        </dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Checklist Expiry</dt>
                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                            {{ schedule.expires_after_minutes ? formatSLA(schedule.expires_after_minutes) : 'No expiry' }}
                        </dd>
                        <dd v-if="schedule.expires_after_minutes" class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                            Generated checklists will automatically expire after this time
                        </dd>
                    </div>
                    <div v-if="locations && locations.length > 0">
                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Location</dt>
                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                            {{ locations.map(l => l.name).join(', ') }}
                        </dd>
                    </div>
                </dl>
            </div>

            <!-- Stats Card -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
                <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Statistics</h2>
                <dl class="space-y-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                    <div>
                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Checklists Generated</dt>
                        <dd class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                            {{ schedule.checklists_generated_count || 0 }}
                        </dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Checklists Completed</dt>
                        <dd class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                            {{ schedule.checklists_completed_count || 0 }}
                        </dd>
                    </div>
                    <div>
                        <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">Completion Rate</dt>
                        <dd class="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                            {{ schedule.completion_rate || 0 }}%
                        </dd>
                    </div>
                </dl>

                <!-- Audit Information -->
                <div class="mt-4">
                    <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Audit Information</h3>
                    <dl class="space-y-3">
                        <div>
                            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Created By</dt>
                            <dd class="mt-0.5 text-sm text-gray-900 dark:text-white">
                                {{ createdByUser ? `${createdByUser.first_name} ${createdByUser.last_name}` : 'N/A' }}
                            </dd>
                        </div>
                        <div>
                            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Created At</dt>
                            <dd class="mt-0.5 text-sm text-gray-900 dark:text-white">
                                {{ formatDateOnly(schedule.created_at) }}
                            </dd>
                        </div>
                        <div v-if="updatedByUser">
                            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Last Modified By</dt>
                            <dd class="mt-0.5 text-sm text-gray-900 dark:text-white">
                                {{ `${updatedByUser.first_name} ${updatedByUser.last_name}` }}
                            </dd>
                        </div>
                        <div v-if="schedule.updated_at && schedule.updated_at !== schedule.created_at">
                            <dt class="text-xs font-medium text-gray-500 dark:text-gray-400">Last Modified At</dt>
                            <dd class="mt-0.5 text-sm text-gray-900 dark:text-white">
                                {{ formatDateOnly(schedule.updated_at) }}
                            </dd>
                        </div>
                    </dl>
                </div>
            </div>
        </div>

        <!-- Checklist Manager Access Control -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Checklist Manager Access Control</h2>
            <div v-if="checklistManagers && checklistManagers.length > 0">
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Access to checklists created by this schedule is restricted to the following Checklist Managers:
                </p>
                <div class="flex flex-wrap gap-2">
                    <span
                        v-for="user in checklistManagers"
                        :key="user.id"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300"
                    >
                        {{ user.name }}
                    </span>
                </div>
            </div>
            <div v-else>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                    All users with the Checklist Manager role can access and manage checklists created by this schedule.
                </p>
            </div>
        </div>

        <!-- Workflow Configuration -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-6">Workflow Configuration</h2>

            <div class="space-y-6">
                <!-- Section 1: To be Conducted/Filled By -->
                <div class="border-b border-gray-200 dark:border-gray-700 pb-6">
                    <h3 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">To be Conducted/Filled By</h3>
                    <div class="space-y-2">
                        <div>
                            <span class="text-sm text-gray-600 dark:text-gray-400">Assignment Type: </span>
                            <span class="text-sm font-medium text-gray-900 dark:text-white capitalize">
                                {{ schedule.conduct_assignment_type?.replace('_', ' ') }}
                            </span>
                        </div>
                        <div v-if="conductUsers && conductUsers.length > 0">
                            <span class="text-sm text-gray-600 dark:text-gray-400">Assigned Users: </span>
                            <div class="mt-1 flex flex-wrap gap-2">
                                <span
                                    v-for="user in conductUsers"
                                    :key="user.id"
                                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                                >
                                    {{ user.first_name }} {{ user.last_name }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Section 2: Approvals -->
                <div class="border-b border-gray-200 dark:border-gray-700 pb-6">
                    <h3 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Approvals</h3>
                    <div v-if="schedule.approval_1_required || schedule.approval_2_required || schedule.approval_3_required">
                        <div class="space-y-3">
                            <div v-if="schedule.approval_1_required" class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                                <div class="flex items-start justify-between">
                                    <div>
                                        <p class="text-sm font-medium text-gray-900 dark:text-white">
                                            {{ schedule.approval_1_step_name || 'Approval Level 1' }}
                                        </p>
                                        <p v-if="approval1User" class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                            Approver: {{ approval1User.first_name }} {{ approval1User.last_name }}
                                        </p>
                                    </div>
                                    <span v-if="schedule.approval_1_auto_approve_hours" class="text-xs text-gray-500 dark:text-gray-400">
                                        Auto-approve: {{ schedule.approval_1_auto_approve_hours }}h
                                    </span>
                                </div>
                            </div>
                            <div v-if="schedule.approval_2_required" class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                                <div class="flex items-start justify-between">
                                    <div>
                                        <p class="text-sm font-medium text-gray-900 dark:text-white">
                                            {{ schedule.approval_2_step_name || 'Approval Level 2' }}
                                        </p>
                                        <p v-if="approval2User" class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                            Approver: {{ approval2User.first_name }} {{ approval2User.last_name }}
                                        </p>
                                    </div>
                                    <span v-if="schedule.approval_2_auto_approve_hours" class="text-xs text-gray-500 dark:text-gray-400">
                                        Auto-approve: {{ schedule.approval_2_auto_approve_hours }}h
                                    </span>
                                </div>
                            </div>
                            <div v-if="schedule.approval_3_required" class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                                <div class="flex items-start justify-between">
                                    <div>
                                        <p class="text-sm font-medium text-gray-900 dark:text-white">
                                            {{ schedule.approval_3_step_name || 'Approval Level 3' }}
                                        </p>
                                        <p v-if="approval3User" class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                            Approver: {{ approval3User.first_name }} {{ approval3User.last_name }}
                                        </p>
                                    </div>
                                    <span v-if="schedule.approval_3_auto_approve_hours" class="text-xs text-gray-500 dark:text-gray-400">
                                        Auto-approve: {{ schedule.approval_3_auto_approve_hours }}h
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p v-else class="text-sm text-gray-500 dark:text-gray-400">No approval levels configured</p>
                </div>

                <!-- Section 3: Notification Upon Completion -->
                <div>
                    <h3 class="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Notification Upon Completion</h3>
                    <div v-if="notifyUsers && notifyUsers.length > 0">
                        <p class="text-sm text-gray-600 dark:text-gray-400 mb-2">The following users will be notified when the checklist is completed:</p>
                        <div class="flex flex-wrap gap-2">
                            <span
                                v-for="user in notifyUsers"
                                :key="user.id"
                                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                            >
                                {{ user.first_name }} {{ user.last_name }}
                            </span>
                        </div>
                    </div>
                    <p v-else class="text-sm text-gray-500 dark:text-gray-400">No notification recipients configured</p>
                </div>
            </div>
        </div>

        <!-- Recent Checklists -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Checklists</h2>
            <div v-if="schedule.checklists && schedule.checklists.length > 0" class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Checklist Code</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Location</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Asset</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Due Date</th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        <tr v-for="checklist in schedule.checklists" :key="checklist.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                {{ checklist.checklist_number }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {{ checklist.location?.name || 'N/A' }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                <span v-if="checklist.asset">{{ checklist.asset.name }} ({{ checklist.asset.asset_tag }})</span>
                                <span v-else>N/A</span>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span :class="getChecklistStatusClass(checklist.status)">
                                    {{ checklist.status }}
                                </span>
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {{ checklist.due_date ? formatDateAndTime(checklist.due_date) : 'N/A' }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {{ formatDateAndTime(checklist.created_at) }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <Link
                                    :href="route('checklists.show', checklist.id)"
                                    class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                                >
                                    View
                                </Link>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <p v-else class="text-sm text-gray-500 dark:text-gray-400">No checklists generated yet</p>
        </div>

        <!-- Pause Confirmation Modal -->
        <div v-if="showPauseModal" class="fixed inset-0 overflow-y-auto" style="z-index: 9999;" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" style="z-index: 9998;" aria-hidden="true" @click="showPauseModal = false"></div>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <div class="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" style="z-index: 10000; position: relative;">
                    <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div class="sm:flex sm:items-start">
                            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 dark:bg-orange-900/30 sm:mx-0 sm:h-10 sm:w-10">
                                <svg class="h-6 w-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">Pause Schedule</h3>
                                <div class="mt-2">
                                    <p class="text-sm text-gray-500 dark:text-gray-400">Are you sure you want to pause this schedule? It will stop generating new checklists until you resume it.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button @click="confirmPause" type="button" class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:ml-3 sm:w-auto sm:text-sm">
                            Pause Schedule
                        </button>
                        <button @click="showPauseModal = false" type="button" class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Resume Schedule Modal with Date/Time Input -->
        <div v-if="showResumeModal" class="fixed inset-0 overflow-y-auto" style="z-index: 9999;" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" style="z-index: 9998;" aria-hidden="true" @click="cancelResume"></div>
                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <div class="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" style="z-index: 10000; position: relative;">
                    <div class="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div class="sm:flex sm:items-start">
                            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 sm:mx-0 sm:h-10 sm:w-10">
                                <svg class="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div class="mt-3 w-full text-center sm:mt-0 sm:ml-4 sm:text-left">
                                <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">Resume Schedule</h3>
                                <div class="mt-2">
                                    <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">Please enter the next execution date and time for this schedule. This must be a future date/time to prevent retroactive execution.</p>

                                    <div class="space-y-4">
                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Next Execution Date</label>
                                            <input
                                                v-model="resumeForm.next_execution_date"
                                                type="date"
                                                class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-green-500 focus:border-green-500"
                                            />
                                        </div>
                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Next Execution Time</label>
                                            <input
                                                v-model="resumeForm.next_execution_time"
                                                type="time"
                                                class="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-green-500 focus:border-green-500"
                                            />
                                        </div>
                                        <div v-if="resumeError" class="text-sm text-red-600 dark:text-red-400">
                                            {{ resumeError }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button @click="confirmResume" type="button" class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm">
                            Resume Schedule
                        </button>
                        <button @click="cancelResume" type="button" class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, router, usePage } from '@inertiajs/vue3';
import { useTimezone } from '@/Composables/useTimezone';
import route from '@/Utils/route';

const props = defineProps({
    schedule: Object,
    locations: Array,
    users: Array, // All users for the company
    checklistManagerUsers: Array,
});

const page = usePage();
const { formatDateTime } = useTimezone();

// Modal states
const showPauseModal = ref(false);
const showResumeModal = ref(false);
const resumeError = ref('');

// Resume form
const resumeForm = ref({
    next_execution_date: '',
    next_execution_time: '',
});

// Get conduct users based on IDs
const conductUsers = computed(() => {
    if (!props.schedule.conduct_user_ids || !props.users) return [];
    return props.users.filter(user => props.schedule.conduct_user_ids.includes(user.id));
});

// Get approval users
const approval1User = computed(() => {
    if (!props.schedule.approval_1_user_id || !props.users) return null;
    return props.users.find(user => user.id === props.schedule.approval_1_user_id);
});

const approval2User = computed(() => {
    if (!props.schedule.approval_2_user_id || !props.users) return null;
    return props.users.find(user => user.id === props.schedule.approval_2_user_id);
});

const approval3User = computed(() => {
    if (!props.schedule.approval_3_user_id || !props.users) return null;
    return props.users.find(user => user.id === props.schedule.approval_3_user_id);
});

// Get notification users
const notifyUsers = computed(() => {
    if (!props.schedule.notify_user_ids || !props.users) return [];
    return props.users.filter(user => props.schedule.notify_user_ids.includes(user.id));
});

// Get checklist managers
const checklistManagers = computed(() => {
    if (!props.schedule.checklist_manager_ids || !props.checklistManagerUsers) return [];
    return props.checklistManagerUsers.filter(user => props.schedule.checklist_manager_ids.includes(user.id));
});

// Get created by user
const createdByUser = computed(() => {
    if (!props.schedule.created_by || !props.users) return null;
    return props.users.find(user => user.id === props.schedule.created_by);
});

// Get updated by user
const updatedByUser = computed(() => {
    if (!props.schedule.updated_by || !props.users) return null;
    return props.users.find(user => user.id === props.schedule.updated_by);
});

// Format date only (dd MMM yyyy)
const formatDateOnly = (date) => {
    if (!date) return 'N/A';
    const dateObj = new Date(date);
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = dateObj.toLocaleDateString('en-US', { month: 'short' });
    const year = dateObj.getFullYear();
    return `${day} ${month} ${year}`;
};

// Format time only (HH:MM) - handles both time strings and datetime objects
const formatTimeOnly = (time) => {
    if (!time) return 'N/A';

    // If it's a time string like "09:00:00", extract HH:MM
    if (typeof time === 'string') {
        const parts = time.split(':');
        if (parts.length >= 2) {
            return `${parts[0]}:${parts[1]}`;
        }
    }

    // If it's a datetime object, format it
    const dateObj = new Date(time);
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

// Format date and time (dd MMM yyyy HH:MM)
const formatDateAndTime = (datetime) => {
    if (!datetime) return 'N/A';
    const dateObj = new Date(datetime);
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = dateObj.toLocaleDateString('en-US', { month: 'short' });
    const year = dateObj.getFullYear();
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    return `${day} ${month} ${year} ${hours}:${minutes}`;
};

const formatSLA = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
        return `${hours}h ${mins}m`;
    } else if (hours > 0) {
        return `${hours}h`;
    } else {
        return `${mins}m`;
    }
};

const getIntervalLabel = (pattern, interval) => {
    const labels = {
        hourly: interval === 1 ? 'hour' : 'hours',
        daily: interval === 1 ? 'day' : 'days',
        weekly: interval === 1 ? 'week' : 'weeks',
        monthly: interval === 1 ? 'month' : 'months',
        yearly: interval === 1 ? 'year' : 'years',
    };
    return labels[pattern] || '';
};

const getStatusClass = (status) => {
    const classes = {
        active: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
        paused: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        completed: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        cancelled: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    };
    return classes[status] || classes.active;
};

const getStatusLabel = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
};

const getChecklistStatusClass = (status) => {
    const classes = {
        draft: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        pending: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        in_progress: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        completed: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
        rejected: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
        cancelled: 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
    };
    return classes[status] || classes.draft;
};

// Pause schedule
const pauseSchedule = () => {
    showPauseModal.value = true;
};

const confirmPause = () => {
    router.post(route('checklists.schedules.pause', props.schedule.id), {
        _company_id: page.props.company_id,
    }, {
        preserveScroll: true,
        onSuccess: () => {
            showPauseModal.value = false;
        },
    });
};

// Resume schedule
const confirmResume = () => {
    resumeError.value = '';

    // Validate inputs
    if (!resumeForm.value.next_execution_date || !resumeForm.value.next_execution_time) {
        resumeError.value = 'Please enter both date and time.';
        return;
    }

    // Combine date and time
    const nextExecutionDateTime = new Date(`${resumeForm.value.next_execution_date}T${resumeForm.value.next_execution_time}`);
    const now = new Date();

    // Validate that it's in the future
    if (nextExecutionDateTime <= now) {
        resumeError.value = 'Next execution time must be in the future.';
        return;
    }

    // Submit the form
    router.post(route('checklists.schedules.resume', props.schedule.id), {
        _company_id: page.props.company_id,
        next_execution_at: nextExecutionDateTime.toISOString(),
    }, {
        preserveScroll: true,
        onSuccess: () => {
            showResumeModal.value = false;
            resumeForm.value = {
                next_execution_date: '',
                next_execution_time: '',
            };
            resumeError.value = '';
        },
        onError: (errors) => {
            if (errors.next_execution_at) {
                resumeError.value = errors.next_execution_at;
            }
        },
    });
};

const cancelResume = () => {
    showResumeModal.value = false;
    resumeForm.value = {
        next_execution_date: '',
        next_execution_time: '',
    };
    resumeError.value = '';
};
</script>
